import {
  AddIcon,
  Button,
  FlexContent,
  InlineOrderEditor,
  ModalDialog,
  TableCell,
  TableHeadCell,
  TableRow,
  useDebounce,
} from "@rothoblaas/design-system";
import { FC, useContext, useState } from "react";
import Icon from "../../../../components/ui/Icon";
import Table from "../../../../components/ui/Table";
import Visibility from "../../../../components/ui/Visibility";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useListFilters from "../../../../hooks/useListFilters";
import useI18n from "../../../../hooks/useTranslations";
import type { Material } from "../../../../http/envelopeDesigner/types/material";
import { useAdminEnvelopeDesignerApi } from "../../../../http/envelopeDesigner/useAdminEnvelopeDesigner";
import { getDeleteAction, getEditAction } from "../../../../utils/rowActions";
import MaterialFormDialog from "./MaterialFormDialog";

// The materials catalog: visibility (hidden materials stay usable by existing calculations),
// order, edit and delete (refused by the server while a calculation or an example uses it).
const MaterialsTab: FC = () => {
  const i18n = useI18n();
  const t = i18n.translation.envelopeDesignerModule.admin;
  const dispatch = useContext(NotificationDispatch);
  const adminApi = useAdminEnvelopeDesignerApi();
  const filters = useListFilters({ isSearchable: true });
  const q = useDebounce(filters.state.q);
  const { data, error } = adminApi.useAdminMaterials({ ...filters.state, q });
  const [editing, setEditing] = useState<Material | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [toDelete, setToDelete] = useState<Material | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const run = async (request: Promise<unknown>) => {
    try {
      await request;
      dispatch(showSuccessNotification(i18n.translation));
      return true;
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
      return false;
    }
  };

  return (
    <FlexContent padding>
      <Table<Material>
        filters={filters}
        search={{ placeholder: `${i18n.translation.common.search}...` }}
        headingActions={
          <Button onClick={() => setIsCreating(true)}>
            <Icon glyph={AddIcon} />
            {i18n.translation.common.add}
          </Button>
        }
        head={
          <TableRow>
            <TableHeadCell align="left">{i18n.translation.common.name}</TableHeadCell>
            <TableHeadCell align="left">{t.manufacturer}</TableHeadCell>
            <TableHeadCell align="left">{i18n.translation.common.category}</TableHeadCell>
            <TableHeadCell align="right">λ [W/(m·K)]</TableHeadCell>
            <TableHeadCell align="right">μ</TableHeadCell>
            <TableHeadCell align="right">ρ [kg/m³]</TableHeadCell>
            <TableHeadCell align="left">{i18n.translation.common.visibility}</TableHeadCell>
            <TableHeadCell align="center">{i18n.translation.common.order}</TableHeadCell>
            <TableHeadCell align="left" />
          </TableRow>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <TableCell>{item.name}</TableCell>
            <TableCell>{item.manufacturer ?? "—"}</TableCell>
            <TableCell>{item.category}</TableCell>
            <TableCell align="right">{item.lambdaDry}</TableCell>
            <TableCell align="right">{item.mu ?? (item.sdHumidityCurve.length ? t.variable : "—")}</TableCell>
            <TableCell align="right">{item.density}</TableCell>
            <TableCell>
              <Visibility
                value={item.visibility as "public" | "internal" | "hidden"}
                onChange={(visibility) => void run(adminApi.updateMaterialVisibility(item.id, visibility))}
              />
            </TableCell>
            <TableCell align="center">
              <InlineOrderEditor
                order={item.order}
                onUpdate={(order) => void run(adminApi.updateMaterialOrder(item.id, order))}
              />
            </TableCell>
          </>
        )}
        rowActions={(item) => [
          getEditAction(i18n, () => setEditing(item)),
          getDeleteAction(i18n, () => setToDelete(item), true),
        ]}
        onRowClick={(item) => setEditing(item)}
        total={data?.total}
        error={!!error}
      />

      {toDelete && (
        <ModalDialog
          onClose={() => setToDelete(null)}
          title={toDelete.name}
          isSubmitting={isSubmitting}
          buttons={[
            <Button key="no" variant="secondary" onClick={() => setToDelete(null)}>
              {i18n.translation.common.cancel}
            </Button>,
            <Button
              key="yes"
              onClick={async () => {
                setIsSubmitting(true);
                if (await run(adminApi.deleteMaterial(toDelete.id))) setToDelete(null);
                setIsSubmitting(false);
              }}
            >
              {i18n.translation.common.yes}
            </Button>,
          ]}
        >
          {t.deleteMaterialQuestion}
        </ModalDialog>
      )}
      {(isCreating || editing) && (
        <MaterialFormDialog
          material={editing}
          onClose={() => {
            setEditing(null);
            setIsCreating(false);
          }}
        />
      )}
    </FlexContent>
  );
};

export default MaterialsTab;
